/**
 * Gamepad API devices (standard mapping, any number of pads, hot-plug).
 *
 * - `PadHub` polls `navigator.getGamepads()` (once per sim tick from the InputManager, plus once per display frame
 *   for menus and hot-plug detection), keeps one `GamepadDevice` per pad index (`pad:<n>`), and emits raw
 *   presses (menus, rebinding capture, activity) with auto-repeat for directions.
 * - Left stick: radial deadzone 0.4 with hysteresis and direction sectors, so running diagonally does not jump
 *   by accident (`stickBits`). Other axes: per-axis threshold with hysteresis.
 * - Tap latching: a button seen down at ANY poll since the last tick is reported for one tick.
 * - `GamepadDevice.sample()` never allocates (bindings are compiled to bit masks).
 */
import { BUTTON_NAMES, type InputFrame } from '@hf/shared';
import { cloneBindings, GAMEPAD_DEFAULT } from './bindings';
import { padFamily, type PadFamily } from './labels';
import { DEVICE_ID, type Bindings, type InputDevice, type MenuAction } from './types';

// ── Platform abstraction (tests provide fakes) ────────────────────────────────────────────────────────
export interface PadButtonLike {
  readonly pressed: boolean;
  readonly value: number;
}
export interface HapticsLike {
  playEffect(type: 'dual-rumble', params: Record<string, number>): Promise<unknown>;
}
export interface GamepadLike {
  readonly index: number;
  readonly id: string;
  readonly connected: boolean;
  readonly mapping: string;
  readonly buttons: readonly PadButtonLike[];
  readonly axes: readonly number[];
  readonly vibrationActuator?: HapticsLike | null;
}
export interface GamepadSource {
  getGamepads(): ArrayLike<GamepadLike | null>;
}

// ── Stick processing ──────────────────────────────────────────────────────────────────────────────────
export const STICK = {
  /** Radial deadzone: the stick must leave this radius before any direction registers. */
  deadzone: 0.4,
  /** Once a horizontal direction is on, it stays on until |x| drops below this (hysteresis). */
  release: 0.3,
  /** Vertical (jump / down) needs a firmer push… */
  verticalPress: 0.6,
  verticalRelease: 0.45,
  /** Horizontal registers within ±67.5° of the horizontal (|y| ≤ |x|·tan 67.5°). */
  horizontalMaxSlope: 2.414,
  /** A held vertical stays on beyond 30° from the horizontal (|y| ≥ |x|·tan 30°, hysteresis)… */
  verticalMinSlope: 0.577,
  /** …a fresh push from neutral registers vertical from 45° (a true diagonal = both)… */
  verticalMinSlopeFresh: 1,
  /**
   * …and while running (a horizontal direction already on) only beyond 50° (tan 50°): a sloppy 40° run must not add
   * ▼ (truck charge) or ▲ (jump / hurdle).
   */
  verticalMinSlopeRunning: 1.192,
  /** Other axes (right stick, analog triggers exposed as axes). */
  axisPress: 0.5,
  axisRelease: 0.35,
  /** Analog buttons (triggers) count as pressed above this value. */
  buttonPress: 0.5,
} as const;

/** Stick direction bits (local to this module): 1 = −x (left), 2 = +x (right), 4 = −y (up), 8 = +y (down). */
export const DIR = { NEG_X: 1, POS_X: 2, NEG_Y: 4, POS_Y: 8 } as const;

/** 4-way digital directions from an analog stick, with deadzone, hysteresis and sectors. Pure, allocation-free. */
export const stickBits = (x: number, y: number, prev: number): number => {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  let out = 0;
  if (ax * ax + ay * ay < STICK.release * STICK.release) return 0;
  const hadH = (prev & (DIR.NEG_X | DIR.POS_X)) !== 0;
  const hadV = (prev & (DIR.NEG_Y | DIR.POS_Y)) !== 0;
  if (ax >= (hadH ? STICK.release : STICK.deadzone) && ay <= ax * STICK.horizontalMaxSlope)
    out |= x < 0 ? DIR.NEG_X : DIR.POS_X;
  const minSlope = hadV
    ? STICK.verticalMinSlope
    : hadH
      ? STICK.verticalMinSlopeRunning
      : STICK.verticalMinSlopeFresh;
  if (
    ay >= (hadV ? STICK.verticalRelease : STICK.verticalPress) &&
    ay >= ax * minSlope &&
    ax * ax + ay * ay >= STICK.deadzone * STICK.deadzone
  )
    out |= y < 0 ? DIR.NEG_Y : DIR.POS_Y;
  return out;
};

/** Per-axis threshold with hysteresis for axes ≥ 2. Returns 0, NEG_X or POS_X (as −/+ of that axis). */
export const axisBits = (v: number, prev: number): number => {
  const a = Math.abs(v);
  const on = a >= (prev ? STICK.axisRelease : STICK.axisPress);
  return on ? (v < 0 ? DIR.NEG_X : DIR.POS_X) : 0;
};

/** Max axes tracked (4 sticks' worth). Axis i, direction d → bit (2i + (d==='+'?1:0)) of the axis mask. */
const MAX_AXES = 8;
/** Max buttons tracked as a 32-bit mask. */
const MAX_BUTTONS = 32;

interface Compiled {
  /** Per logical button (BUTTON_NAMES order): physical button bits. */
  buttons: number[];
  /** Per logical button: axis-direction bits. */
  axes: number[];
}

const compile = (b: Bindings): Compiled => {
  const buttons: number[] = [];
  const axes: number[] = [];
  for (const name of BUTTON_NAMES) {
    let bm = 0;
    let am = 0;
    for (const code of b[name]) {
      const ax = /^axis(\d)([-+])$/.exec(code);
      if (ax) {
        const i = Number(ax[1]);
        if (i < MAX_AXES) am |= 1 << (i * 2 + (ax[2] === '+' ? 1 : 0));
      } else {
        const n = Number(code);
        if (Number.isInteger(n) && n >= 0 && n < MAX_BUTTONS) bm |= 1 << n;
      }
    }
    buttons.push(bm >>> 0);
    axes.push(am);
  }
  return { buttons, axes };
};

/** Raw physical state of one pad at the latest poll. */
export interface PadSnapshot {
  /** Buttons currently down (bit n = button n). */
  buttons: number;
  /** Axis directions currently active (bit 2i = axis i negative, 2i+1 = positive). */
  axes: number;
}

export class GamepadDevice implements InputDevice {
  readonly kind = 'gamepad' as const;
  readonly id: string;
  connected = true;
  label: string;
  family: PadFamily;
  standard: boolean;
  /** Raw state at the latest poll. */
  readonly raw: PadSnapshot = { buttons: 0, axes: 0 };
  private compiled: Compiled;
  private _bindings: Bindings;
  /** Logical frame at the latest poll, and everything seen since the last endTick (tap latch). */
  private held = 0;
  private latch = 0;

  constructor(
    readonly index: number,
    gamepadId: string,
    mapping: string,
    bindings: Bindings = GAMEPAD_DEFAULT,
  ) {
    this.id = DEVICE_ID.pad(index);
    this.family = padFamily(gamepadId);
    this.standard = mapping === 'standard';
    this.label = GamepadDevice.describe(index, gamepadId, this.family, this.standard);
    this._bindings = cloneBindings(bindings);
    this.compiled = compile(this._bindings);
  }

  static describe(index: number, gamepadId: string, family: PadFamily, standard: boolean): string {
    const name =
      family === 'playstation'
        ? 'PlayStation pad'
        : family === 'xbox'
          ? 'Xbox pad'
          : family === 'nintendo'
            ? 'Switch pad'
            : (gamepadId.replace(/\s*\(.*\)\s*/g, ' ').trim() || 'Gamepad').slice(0, 24);
    return `${name} ${index + 1}${standard ? '' : ' (generic)'}`;
  }

  get bindings(): Bindings {
    return this._bindings;
  }

  set bindings(b: Bindings) {
    this._bindings = cloneBindings(b);
    this.compiled = compile(this._bindings);
    this.held = this.toLogical(this.raw.buttons, this.raw.axes);
  }

  /** Update from a fresh raw snapshot (called by PadHub.poll). */
  update(buttons: number, axes: number): void {
    this.raw.buttons = buttons;
    this.raw.axes = axes;
    this.held = this.toLogical(buttons, axes);
    this.latch |= this.held;
  }

  /** Disconnected: release everything (a pulled cable must not leave "right" held). */
  release(): void {
    this.raw.buttons = 0;
    this.raw.axes = 0;
    this.held = 0;
    this.latch = 0;
  }

  private toLogical(buttons: number, axes: number): InputFrame {
    const { buttons: bm, axes: am } = this.compiled;
    let f = 0;
    for (let i = 0; i < bm.length; i++) if ((buttons & bm[i]!) !== 0 || (axes & am[i]!) !== 0) f |= 1 << i;
    return f;
  }

  sample(): InputFrame {
    return this.connected ? this.held | this.latch : 0;
  }

  endTick(): void {
    this.latch = 0;
  }

  dispose(): void {
    this.release();
  }
}

/** Menu actions in a fixed order (index = bit in the per-pad menu state). */
const MENU_ACTIONS: readonly MenuAction[] = ['up', 'down', 'left', 'right', 'confirm', 'back', 'pause'];
/** Raw standard-mapping button per menu action (not rebindable). */
const MENU_BUTTON_OF: readonly number[] = [12, 13, 14, 15, 0, 1, 9];
/** Left-stick direction per menu action (0 = none). */
const MENU_STICK_OF: readonly number[] = [DIR.NEG_Y, DIR.POS_Y, DIR.NEG_X, DIR.POS_X, 0, 0, 0];
/** Directions auto-repeat while held; confirm/back/pause fire once per press. */
const REPEATABLE_COUNT = 4;
const SELECT_BUTTON = 8;
const RIGHT_STICK_BUTTON = 11;

export const MENU_REPEAT = { delayMs: 380, intervalMs: 110 } as const;

export interface PadHubEvents {
  /** A pad connected/disconnected. */
  devicesChanged(): void;
  /** Menu action (with auto-repeat for directions). */
  menu(action: MenuAction, pad: GamepadDevice): void;
  /** Back/Select button. */
  select(pad: GamepadDevice): void;
  /** Right-stick click (R3). */
  rightStickClick?(pad: GamepadDevice): void;
  /** Any new raw press (button index as string, or "axisN±"): rebinding capture + activity tracking. */
  rawPress(pad: GamepadDevice, code: string): void;
}

interface RepeatState {
  /** Menu actions held at the last poll (bit a = MENU_ACTIONS[a]). */
  held: number;
  /** Next auto-repeat time per action. */
  next: number[];
}

export class PadHub {
  private readonly pads = new Map<number, GamepadDevice>();
  private readonly repeat = new Map<number, RepeatState>();
  private readonly overrides = new Map<string, Bindings>();
  private connectedList: GamepadDevice[] = [];
  /** Per pad: previous raw (for raw-press edges). */
  private readonly prevRaw = new Map<number, PadSnapshot>();
  /** Left-stick + other axes hysteresis state per pad (direction bits per axis pair). */
  private readonly axisState = new Map<number, number>();

  constructor(
    private readonly source: GamepadSource | null,
    private readonly events: PadHubEvents,
  ) {}

  /** Connected pads, in index order. */
  get connected(): readonly GamepadDevice[] {
    return this.connectedList;
  }

  device(index: number): GamepadDevice | undefined {
    return this.pads.get(index);
  }

  setBindings(index: number, b: Bindings | null): void {
    const key = DEVICE_ID.pad(index);
    if (b) this.overrides.set(key, cloneBindings(b));
    else this.overrides.delete(key);
    const pad = this.pads.get(index);
    if (pad) pad.bindings = b ?? GAMEPAD_DEFAULT;
  }

  bindingsOverride(index: number): Bindings | undefined {
    return this.overrides.get(DEVICE_ID.pad(index));
  }

  /**
   * Read every pad once. `now` (ms) drives menu auto-repeat. `emit` false = update state only (the per-tick
   * poll inside a match still emits so Start pauses immediately; the flag exists for tests).
   */
  poll(now: number, emit = true): void {
    const src = this.source;
    if (!src) return;
    let list: ArrayLike<GamepadLike | null>;
    try {
      list = src.getGamepads();
    } catch {
      return; // permissions policy may block the API
    }
    let changed = false;
    // Disconnections: any known connected pad missing from the list.
    for (const pad of this.connectedList) {
      const gp = list[pad.index];
      if (!gp || !gp.connected) {
        pad.connected = false;
        pad.release();
        this.prevRaw.delete(pad.index);
        this.repeat.delete(pad.index);
        this.axisState.delete(pad.index);
        changed = true;
      }
    }
    for (let i = 0; i < list.length; i++) {
      const gp = list[i];
      if (!gp || !gp.connected) continue;
      let pad = this.pads.get(gp.index);
      if (!pad) {
        pad = new GamepadDevice(
          gp.index,
          gp.id,
          gp.mapping,
          this.overrides.get(DEVICE_ID.pad(gp.index)) ?? GAMEPAD_DEFAULT,
        );
        this.pads.set(gp.index, pad);
        changed = true;
      } else if (!pad.connected) {
        pad.connected = true;
        pad.family = padFamily(gp.id);
        pad.standard = gp.mapping === 'standard';
        pad.label = GamepadDevice.describe(gp.index, gp.id, pad.family, pad.standard);
        changed = true;
      }
      this.read(pad, gp, now, emit);
    }
    if (changed) {
      this.connectedList = [...this.pads.values()]
        .filter((p) => p.connected)
        .sort((a, b) => a.index - b.index);
      this.events.devicesChanged();
    }
  }

  private read(pad: GamepadDevice, gp: GamepadLike, now: number, emit: boolean): void {
    let buttons = 0;
    const n = Math.min(gp.buttons.length, MAX_BUTTONS);
    for (let b = 0; b < n; b++) {
      const btn = gp.buttons[b]!;
      if (btn.pressed || btn.value > STICK.buttonPress) buttons |= 1 << b;
    }
    buttons >>>= 0;
    const prevAxes = this.axisState.get(pad.index) ?? 0;
    let axes = 0;
    const ax = gp.axes;
    if (ax.length >= 2) axes |= stickBits(ax[0] ?? 0, ax[1] ?? 0, prevAxes & 0xf);
    for (let a = 2; a < Math.min(ax.length, MAX_AXES); a++) {
      const bits = axisBits(ax[a] ?? 0, (prevAxes >> (a * 2)) & 3);
      axes |= bits << (a * 2);
    }
    this.axisState.set(pad.index, axes);
    pad.update(buttons, axes);

    let prev = this.prevRaw.get(pad.index);
    if (!prev) {
      prev = { buttons: 0, axes: 0 };
      this.prevRaw.set(pad.index, prev);
    }
    const newButtons = (buttons & ~prev.buttons) >>> 0;
    const newAxes = axes & ~prev.axes;
    prev.buttons = buttons;
    prev.axes = axes;
    if (!emit) return;

    if (newButtons !== 0 || newAxes !== 0) {
      for (let b = 0; b < MAX_BUTTONS; b++) if (newButtons & (1 << b)) this.events.rawPress(pad, String(b));
      for (let a = 0; a < MAX_AXES; a++) {
        if (newAxes & (1 << (a * 2))) this.events.rawPress(pad, `axis${a}-`);
        if (newAxes & (1 << (a * 2 + 1))) this.events.rawPress(pad, `axis${a}+`);
      }
      if (newButtons & (1 << SELECT_BUTTON)) this.events.select(pad);
      if (newButtons & (1 << RIGHT_STICK_BUTTON)) this.events.rightStickClick?.(pad);
    }
    this.menu(pad, buttons, axes & 0xf, now);
  }

  private menu(pad: GamepadDevice, buttons: number, stick: number, now: number): void {
    let st = this.repeat.get(pad.index);
    if (!st) {
      st = { held: 0, next: MENU_ACTIONS.map(() => 0) };
      this.repeat.set(pad.index, st);
    }
    let held = 0;
    for (let a = 0; a < MENU_ACTIONS.length; a++) {
      const d = MENU_STICK_OF[a]!;
      if ((buttons & (1 << MENU_BUTTON_OF[a]!)) !== 0 || (d !== 0 && (stick & d) !== 0)) held |= 1 << a;
    }
    const prev = st.held;
    st.held = held;
    for (let a = 0; a < MENU_ACTIONS.length; a++) {
      const bit = 1 << a;
      if ((held & bit) === 0) continue;
      if ((prev & bit) === 0) {
        st.next[a] = now + MENU_REPEAT.delayMs;
        this.events.menu(MENU_ACTIONS[a]!, pad);
      } else if (a < REPEATABLE_COUNT && now >= st.next[a]!) {
        st.next[a] = Math.max(st.next[a]! + MENU_REPEAT.intervalMs, now - MENU_REPEAT.intervalMs);
        this.events.menu(MENU_ACTIONS[a]!, pad);
      }
    }
  }

  /** End of a sim tick: clear every pad's tap latch. */
  endTick(): void {
    for (const pad of this.connectedList) pad.endTick();
  }

  rumble(index: number, strength: number, durationMs: number): void {
    const src = this.source;
    if (!src) return;
    try {
      const gp = src.getGamepads()[index];
      const s = Math.max(0, Math.min(1, strength));
      void gp?.vibrationActuator
        ?.playEffect('dual-rumble', {
          startDelay: 0,
          duration: Math.max(0, Math.round(durationMs)),
          weakMagnitude: s,
          strongMagnitude: s * 0.8,
        })
        .catch(() => {});
    } catch {
      /* haptics unsupported */
    }
  }

  dispose(): void {
    for (const p of this.pads.values()) p.dispose();
    this.pads.clear();
    this.connectedList = [];
  }
}
